import React, { useState, useEffect } from 'react';
import { Calendar, Filter, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/sonner';
import { overallProgress } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConstructionUpdates = () => {
  const [selectedYear, setSelectedYear] = useState('all');
  const [allUpdates, setAllUpdates] = useState([]);
  const [filteredUpdates, setFilteredUpdates] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState(['all']);

  useEffect(() => {
    fetchAllUpdates();
  }, []);

  const fetchAllUpdates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/construction-updates`);
      setAllUpdates(response.data);
      setFilteredUpdates(response.data);
      
      // Extract unique years
      const uniqueYears = [...new Set(response.data.map(update => update.year))];
      setYears(['all', ...uniqueYears.sort((a, b) => b - a)]);
    } catch (error) {
      console.error('Error fetching construction updates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedYear === 'all') {
      setFilteredUpdates(allUpdates);
    } else {
      setFilteredUpdates(allUpdates.filter(update => update.year === parseInt(selectedYear)));
    }
  }, [selectedYear, allUpdates]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Page Hero */}
      <section className="pt-20 pb-6 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-gray-600 hover:text-orange-600 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </div>

          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-2">
              Construction Progress <span className="text-orange-600">Timeline</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              Building Changuna Aangan - Every Step of the Way
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Stats Bar */}
      <section className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Filters */}
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700 mr-2">Filter by Year:</span>
              <div className="flex space-x-2">
                {years.map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedYear === year
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {year === 'all' ? 'All' : year}
                  </button>
                ))}
              </div>
            </div>

            {/* Progress */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Overall Progress</p>
                <p className="text-2xl font-bold text-orange-600">{overallProgress}%</p>
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-3">
                <div
                  className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transition-all duration-500"
                  style={{ width: `${overallProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="text-center py-20">
                <p className="text-gray-600 text-lg">Loading construction updates...</p>
              </div>
            ) : (
              <>
                {/* Timeline */}
                <div className="relative">
                  {/* Vertical Line */}
                  <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-200 via-orange-300 to-orange-200"></div>

                  {/* Timeline Items */}
                  <div className="space-y-16">
                    {filteredUpdates.map((update, index) => (
                      <div
                        key={update._id}
                        className={`relative flex items-start ${
                          index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                        } flex-col`}
                      >
                        {/* Timeline Dot */}
                        <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 -translate-x-1/2 md:translate-x-0 w-6 h-6 bg-orange-500 rounded-full border-4 border-white shadow-lg z-10"></div>

                        {/* Content Card */}
                        <div
                          className={`w-full md:w-5/12 ml-20 md:ml-0 ${
                            index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'
                          }`}
                        >
                          <div className="bg-white rounded-2xl shadow-xl border-2 border-orange-100 overflow-hidden hover:border-orange-300 transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-1">
                            {/* Image */}
                            <div
                              className="relative h-64 overflow-hidden cursor-pointer group"
                              onClick={() => setLightboxImage(update.image_url)}
                            >
                              <img
                                src={update.image_url}
                                alt={update.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <span className="text-white font-semibold bg-orange-500 px-4 py-2 rounded-lg">View Full Image</span>
                              </div>
                              
                              {/* Category Badge */}
                              {update.category && (
                                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-orange-600 px-3 py-1 rounded-full text-xs font-semibold">
                                  {update.category}
                                </div>
                              )}
                              
                              {/* Progress Badge */}
                              <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                {update.progress}% Complete
                              </div>
                            </div>

                            {/* Content */}
                            <div className="p-6">
                              <div className="flex items-center space-x-2 text-orange-600 mb-3">
                                <Calendar className="w-4 h-4" />
                                <span className="text-sm font-semibold">{update.displayDate}</span>
                              </div>
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                                {update.title}
                              </h3>
                              <p className="text-gray-600 leading-relaxed">
                                {update.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* No Results */}
                {filteredUpdates.length === 0 && !loading && (
                  <div className="text-center py-20">
                    <p className="text-gray-500 text-lg">No construction updates found for the selected year.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Want to See the Progress in Person?
          </h2>
          <p className="text-lg text-orange-100 mb-8 max-w-2xl mx-auto">
            Schedule a site visit and witness the quality firsthand
          </p>
          <Button
            onClick={scrollToContact}
            className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            Schedule Site Visit
          </Button>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-4xl hover:text-orange-500 transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Construction Update"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ConstructionUpdates;